import React, { Component } from 'react'
import { Trans } from '@lingui/react'
import Spacer from 'client/shared/blocks/spacer'
import { Heading } from 'client/shared/blocks/heading'
import { postApplyOffer } from 'client/bookmate/reducers/subscription-reducer'
import { connect } from 'react-redux'
import { Dispatch, State as ConnectedState } from 'shared/types/redux'
import {
  UnsubscribeState,
  loadBestsellersResources,
} from 'client/bookmate/reducers/unsubscribe-reducer'
import RecurrentSubscriptionCard from '../recurrent-subscription-card/recurrent-subscription-card'
import Button from 'client/shared/blocks/button/button'
import './manage-subscriptions.styl'
import { CurrentUserSubscriptions } from 'client/shared/types/subscription'
import animateScrollTo from 'animated-scroll-to'
import { MANAGE_HEAD_TITLE_ID } from './manage-subscriptions'
import { ReadablePrice } from 'client/shared/blocks/readable-price/readable-price'
import HorizontalScroller from 'client/shared/blocks/horizontal-scroller/horizontal-scroller'
import { BookCover } from 'client/shared/blocks/book-cover/book-cover'
import Linka from 'client/shared/blocks/linka/linka'
import urlHelper from 'shared/tools/url-helper'
import BookStatus from 'client/shared/blocks/book-status/book-status'
import { Locale } from 'client/shared/types/current-user'
import BookIcon from 'client/shared/icons/book.svg'
import FlagIcon from 'client/shared/icons/flag.svg'
import TranslateIcon from 'client/shared/icons/translate.svg'
import SVGInline from 'react-svg-inline'
import { Loader } from 'client/shared/blocks/loader/loader'
import { getShowcaseLink } from 'client/bookmate/helpers/showcase-helpers'
import { ResourceProps, ShowcaseResource } from 'client/shared/types/resource'

type Props = {
  dispatch: Dispatch
  unsubscribeProcess?: UnsubscribeState
  closeOffer: () => void
  cancelRecurrent: () => void
  userSubscriptions?: CurrentUserSubscriptions
  locale: Locale
  noInterest?: boolean
  libraryParams?: LibraryParams
}

const STEP_DISCOUNT = 'discount'
const STEP_BENEFITS = 'benefits'
const STEP_BOOKS = 'books'

type OfferState = 'books' | 'benefits' | 'discount'
type State = { step: OfferState }

const connectWrapper = connect((state: ConnectedState) => ({
  unsubscribeProcess: state.unsubscribeProcess,
  userSubscriptions: state.subscription.userSubscriptions,
}))

const BENEFITS = [
  { title: 'access_heading', text: 'access_desc', icon: BookIcon },
  {
    title: 'finish_your_books_heading',
    text: 'finish_your_books_desc',
    icon: FlagIcon,
    extra: true,
  },
  { title: 'translator_heading', text: 'translator_desc', icon: TranslateIcon },
]

const BESTSELLER_IDS = {
  ru: 'bestsellery',
  en: 'bestsellers',
  hr: 'top-naslovi',
  da: 'bestsellers',
  de: 'belletristik',
  es: 'popular',
  id: 'seleksi-bacaan-bahasa-inggris',
  nl: 'nl_popular',
  pt: 'novo-no-bookmate',
  sr: 'sr_bestsellers',
  sv: 'sv_veckans_axplock',
  tr: 'tr_karsilama_ekibi',
  uk: 'ukr_bestsellers',
  bg: 'izbrani-tazi-sedmitsa',
  hu: 'pick-s-a-het',
}

export type LibraryParams = { content_type: string; slug: string }

export const getBestsellers = async ({
  locale,
  dispatch,
  libraryParams,
  ids,
  append,
}: {
  locale: Locale
  dispatch: Dispatch
  libraryParams: LibraryParams
  ids?: Record<string, string>
  append?: boolean
}): Promise<ResourceProps[]> => {
  const resources = await dispatch(
    loadBestsellersResources({
      sectionSlug: Boolean(ids)
        ? ids[locale || 'en']
        : BESTSELLER_IDS[locale || 'en'],
      showcaseSlug: libraryParams.slug,
      libraryLang: locale,
      contentType: libraryParams.content_type as ShowcaseResource,
      append,
    }),
  )
  return resources
}

export class _RetentionOffer extends Component<Props, State> {
  state = {
    step: 'books' as OfferState,
  }
  cardTop: JSX.Element | null | undefined
  componentDidMount(): void {
    const target = document.getElementById(MANAGE_HEAD_TITLE_ID)
    if (!target) return
    animateScrollTo(target.getBoundingClientRect().top - 88, { speed: 300 })
  }
  render(): JSX.Element {
    return (
      <>
        <Spacer size="after-heading" />
        {this.getStep()}
      </>
    )
  }

  getStep(): JSX.Element | null {
    const stepName = this.state.step
    switch (stepName) {
      case STEP_BOOKS:
        return this.getOfferBooksCards()
      case STEP_BENEFITS:
        return this.getBenefits()
      case STEP_DISCOUNT:
        return this.getOfferSubscriptionCards()
      default:
        return null
    }
  }

  getBenefits(): JSX.Element {
    const lastTenReading = this.props.unsubscribeProcess?.retentionOfferData
      ?.last_10_reading_books?.data
    const showBooks = Number(lastTenReading?.length) > 0
    return (
      <>
        <Heading rank={2}>
          <Trans id="subscription.cancel_subscription" />
        </Heading>
        <Spacer size={24} />
        <div className="product-card_horizontal retention-offer">
          <span className="cancellation-caption">
            <Trans id="subscription.step" values={{ maxSteps: 3, step: 3 }} />
          </span>
          <Spacer />
          <h4 className="offer-heading">
            <Trans id="subscription.cancelling_means" />{' '}
          </h4>
          <Spacer size={24} />
          <div>
            {BENEFITS.map(benefit =>
              !showBooks && benefit.extra ? null : (
                <div className="benefit-grid">
                  <SVGInline svg={benefit.icon} className="benefit-grid_icon" />
                  <h6 className="benefit-grid_heading">
                    <Trans id={`subscription.${benefit.title}`} />
                  </h6>
                  <p className="benefit-grid_text">
                    <Trans id={`subscription.${benefit.text}`} />
                    {benefit.extra ? (
                      /* @ts-ignore */
                      <HorizontalScroller>
                        {lastTenReading?.map(book => (
                          <Linka
                            path={urlHelper[
                              book.attributes?.type?.toLocaleLowerCase()
                            ](book.attributes.uuid)}
                            key={book.attributes.uuid}
                          >
                            <BookStatus
                              progress={book.attributes.progress}
                              isPrivate={book.attributes.private}
                            />
                            <BookCover
                              isInSlider
                              book={{
                                cover: {
                                  url: book.attributes.cover.url,
                                  uuid: book.attributes.uuid,
                                },
                              }}
                            />
                          </Linka>
                        ))}
                      </HorizontalScroller>
                    ) : null}
                  </p>
                  <Spacer size={24} />
                </div>
              ),
            )}
          </div>
          <Button
            kind="secondary"
            type="inline"
            className="button__keep"
            onClick={this.props.closeOffer}
          >
            <Trans id="subscription.keep_subscription" />
          </Button>
          <Button
            kind="transparent"
            type="inline"
            className="button__cancel"
            onClick={() => this.setStep(STEP_DISCOUNT)}
          >
            <Trans id="subscription.done" />
          </Button>
        </div>
      </>
    )
  }

  getOfferBooksCards(): JSX.Element | null {
    if (!this.props.unsubscribeProcess) return null
    const { retentionOfferData } = this.props.unsubscribeProcess

    if (
      !this.props.noInterest &&
      (!retentionOfferData || retentionOfferData?.loading)
    )
      return <Loader color="gray" kind="big" />

    const showBestsellers =
      !retentionOfferData?.total_finished_books_purchased_not_uploads ||
      retentionOfferData?.total_finished_books_purchased_not_uploads === 0 ||
      this.props.noInterest
    const showInterestOffer =
      Number(retentionOfferData?.active_subscription_per_month_price) >=
        Number(retentionOfferData?.total_books_in_store_price) ||
      this.props.noInterest

    if (
      (showBestsellers || showInterestOffer) &&
      !retentionOfferData?.bestsellers &&
      !retentionOfferData?.loading
    ) {
      getBestsellers({
        libraryParams: this.props.libraryParams as LibraryParams,
        dispatch: this.props.dispatch,
        locale: this.props.locale,
      })
    }

    const offerData =
      showBestsellers || showInterestOffer
        ? retentionOfferData?.bestsellers
        : retentionOfferData?.last_10_finished_books?.data
    if (!offerData?.length) return <Loader color="gray" kind="big" />

    const currentDuration =
      this.props.userSubscriptions?.recurrents.find(
        subsc => subsc.autorenew_enabled,
      )?.duration || '1_month'

    return (
      <>
        <Heading rank={2}>
          <Trans id="subscription.cancel_subscription" />
        </Heading>
        <Spacer size={24} />
        <div className="product-card_horizontal retention-offer">
          <span className="cancellation-caption">
            <Trans
              id="subscription.step"
              values={{ maxSteps: this.props.noInterest ? 2 : 3, step: 2 }}
            />
          </span>
          <Spacer size={24} />
          {showInterestOffer ? (
            <h4 className="offer-heading">
              <Trans id="subscription.interest_offer" />
            </h4>
          ) : (
            <>
              <h4 className="offer-heading">
                <Trans
                  id="subscription.saved_money"
                  values={{
                    booksCount:
                      retentionOfferData?.total_finished_books_purchased_not_uploads,
                    duration: <Trans id={`plan.per_${currentDuration}`} />,
                    price: (
                      <ReadablePrice
                        kind="short"
                        price={Number(
                          retentionOfferData?.active_subscription_per_month_price,
                        )}
                        currency={
                          retentionOfferData?.active_subscription_per_month_currency
                        }
                      />
                    ),
                  }}
                />
              </h4>
              <div className="offer-prices_grid">
                <div className="offer-prices">
                  <ReadablePrice
                    className="per_month_main blue"
                    kind="short"
                    price={Number(
                      retentionOfferData?.active_subscription_per_month_price,
                    )}
                    currency={
                      retentionOfferData?.active_subscription_per_month_currency
                    }
                  />
                  <br />
                  <span className="per_month_caption">
                    <Trans
                      id="subscription.subscription_per_month"
                      values={{
                        duration: <Trans id={`plan.per_${currentDuration}`} />,
                      }}
                    />
                  </span>
                </div>
                <div className="offer-prices red">
                  <ReadablePrice
                    kind="short"
                    price={Number(
                      retentionOfferData?.total_books_in_store_price,
                    )}
                    currency={
                      retentionOfferData?.active_subscription_per_month_currency
                    }
                    className="per_month_main red"
                  />
                  <br />
                  <span className="per_month_caption">
                    <Trans
                      id="subscription.books_in_the_bookstore"
                      values={{
                        count:
                          retentionOfferData?.total_finished_books_purchased_not_uploads,
                      }}
                    />
                  </span>
                </div>
              </div>
            </>
          )}
          <Spacer size={32} />
          {showBestsellers && (
            <Linka
              className="showcase_box"
              path={urlHelper.showcaseSection(
                getShowcaseLink(
                  this.props.libraryParams?.content_type as ShowcaseResource,
                  this.props.libraryParams?.slug,
                ),
                BESTSELLER_IDS[this.props.locale || 'en'],
              )}
            >
              <span className="showcase_title">
                <b>
                  <Trans id="landing.showcase_title" />
                </b>
              </span>
              <span className="all-header__button">
                <Trans id="auth.all" />
              </span>
            </Linka>
          )}
          <Spacer />
          {/* @ts-ignore */}
          <HorizontalScroller asList>
            {offerData.map(book => {
              const authors = book.authors_objects || book.attributes.author
              return (
                <li
                  key={book.uuid || book.attributes.uuid}
                  className="offer-book"
                >
                  <Linka
                    path={urlHelper.book(book.uuid || book.attributes.uuid)}
                  >
                    <BookStatus
                      isFinished={
                        book.attributes?.book_state === 'finished' || false
                      }
                      progress={book.attributes?.progress || 0}
                      isPrivate={book.attributes?.private || false}
                    />
                    <BookCover
                      isInSlider
                      book={{
                        cover: {
                          url: book.cover?.small || book.attributes.cover.url,
                          uuid: book.uuid || book.attributes.uuid,
                        },
                      }}
                    />
                  </Linka>
                  <Spacer size={8} />
                  <Linka
                    path={urlHelper.book(book.uuid || book.attributes.uuid)}
                    key={book.uuid || book.attributes.uuid}
                    className="offer_book"
                  >
                    {book.title || book.attributes.title}
                  </Linka>
                  <div className="offer_book">
                    {authors.map((author, index) => (
                      <Linka path={urlHelper.author(author.uuid)}>
                        <b>
                          {author.name}
                          {index !== authors.length - 1 && ', '}
                        </b>
                      </Linka>
                    ))}
                  </div>
                </li>
              )
            })}
          </HorizontalScroller>
          <Spacer />
          <Button
            kind="secondary"
            type="inline"
            className="button__keep"
            onClick={this.props.closeOffer}
          >
            <Trans id="subscription.keep_subscription" />
          </Button>
          <Button
            kind="transparent"
            type="inline"
            className="button__cancel"
            onClick={() =>
              this.props.noInterest
                ? this.props.cancelRecurrent()
                : this.setStep(STEP_BENEFITS)
            }
          >
            <Trans
              id={`subscription.${
                this.props.noInterest ? 'no_to_discount_subscription' : 'next'
              }`}
            />
          </Button>
        </div>
      </>
    )
  }

  setStep(step: OfferState): void {
    this.setState({ step })
    window.scrollTo(0, 0)
  }

  applyOffer(): void {
    const { dispatch, closeOffer } = this.props
    closeOffer()
    dispatch(postApplyOffer())
  }

  getOfferSubscriptionCards(): JSX.Element {
    const InnerText = () => (
      <>
        <span className="offer-title">
          <Trans id="subscription.stay_offer" />
        </span>
        <Spacer />
        <span>
          <Trans
            id="subscription.stay_discount"
            components={[<span className="offer-highlight" />]}
            values={{ discount: 20 }}
          />
        </span>
      </>
    )

    const ActionButtons = () => (
      <div className="subscription-buttons_row discount">
        <Button onClick={this.applyOffer.bind(this)}>
          <Trans id="subscription.discount_subscription" />
        </Button>
        <Button
          kind="transparent"
          type="inline"
          className="button__cancel"
          onClick={this.props.cancelRecurrent}
        >
          <Trans id="subscription.no_to_discount_subscription" />
        </Button>
      </div>
    )

    const activeSubscription = this.props.userSubscriptions?.recurrents.find(
      plan => plan.autorenew_enabled,
    )

    return (
      <>
        <Heading rank={2}>
          <Trans id="subscription.offer" />
        </Heading>
        <Spacer size={24} />
        <RecurrentSubscriptionCard
          offerDiscount={this.state.step === STEP_DISCOUNT}
          subscription={activeSubscription}
          Inner={InnerText}
          ActionButtons={ActionButtons}
        />
      </>
    )
  }
}

export const RetentionOffer: React.FC<Props> = connectWrapper(_RetentionOffer)
